import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Preloader from "../helper/Preloader";
import HeaderOne from "../components/HeaderOne";
import ProductDetailsOne from "../components/ProductDetailsOne";
import ShippingOne from "../components/ShippingOne";
import FooterOne from "../components/FooterOne";
import BottomFooter from "../components/BottomFooter";
import Breadcrumb from "../components/Breadcrumb";
import ScrollToTop from "react-scroll-to-top";
import ColorInit from "../helper/ColorInit";
import productService from "../services/productService";

const ProductDetailsPageOne = () => {
  const { id } = useParams(); // Bu parametre artık slug veya ID olabilir
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Görsel URL'lerini birleştirmek için API base URL'i
  const imageApiUrl = (process.env.REACT_APP_IMAGE_API_URL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/,'');

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Önce slug olarak dene (UUID değilse slug'dır)
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
        let response;
        
        if (isUUID) {
          // UUID ise ID ile ara
          response = await productService.getProductById(id);
        } else {
          // UUID değilse slug ile ara
          response = await productService.getProductBySlug(id);
        }
        
        if (response && response.succeeded) {
          setProduct(response.product);
        }
      } catch (error) {
        console.error('Ürün detayları yüklenemedi:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // SEO meta verilerini hazırla
  const getMetaData = () => {
    if (!product) {
      return {
        title: "Ürün Detayı | Gümüş Pestil",
        description: "Gümüşhane'den en kaliteli pestil ve köme ürünlerini keşfedin.",
        keywords: "pestil, köme, gümüşhane, doğal, organik",
        image: `${process.env.REACT_APP_API_URL}/assets/images/default-product.jpg`
      };
    }

    const title = product.metaTitle || `${product.name} | Gümüş Pestil`;
    const description = product.metaDescription || product.shortDescription || product.description || 
      `${product.name} - Gümüşhane'den en kaliteli pestil ve köme ürünleri. Doğal ve sağlıklı lezzetler.`;
    const keywords = product.tags ? product.tags.join(', ') : 
      `${product.name}, pestil, köme, gümüşhane, doğal, organik, ${product.category?.name || ''}`;
    const image = product.imageUrl ? 
      (product.imageUrl.startsWith('http') ? product.imageUrl : `${imageApiUrl}${product.imageUrl}`) :
      `${process.env.REACT_APP_API_URL}/assets/images/default-product.jpg`;

    return { title, description, keywords, image };
  };

  const metaData = getMetaData();
  const currentUrl = `${window.location.origin}/product-details/${id}`;

  return (
    <>
      <Helmet>
        {/* Temel Meta Taglar */}
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
        <meta name="keywords" content={metaData.keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph Meta Taglar (Facebook, WhatsApp, vb.) */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:image" content={metaData.image} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:site_name" content="Gümüş Pestil" />
        
        {/* Ürün özgü Open Graph taglar */}
        {product && (
          <>
            <meta property="product:price:amount" content={product.discountedPrice || product.price} />
            <meta property="product:price:currency" content="TRY" />
            <meta property="product:availability" content={product.stockQuantity > 0 ? "in stock" : "out of stock"} />
            <meta property="product:condition" content="new" />
            <meta property="product:brand" content="Gümüş Pestil" />
            <meta property="product:category" content={product.category?.name || "Pestil & Köme"} />
            {product.barcode && <meta property="product:ean" content={product.barcode} />}
          </>
        )}

        {/* Twitter Card Meta Taglar */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaData.title} />
        <meta name="twitter:description" content={metaData.description} />
        <meta name="twitter:image" content={metaData.image} />

        {/* JSON-LD Yapılandırılmış Veri */}
        {product && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Product",
              "name": product.name,
              "description": product.description,
              "image": metaData.image,
              "brand": {
                "@type": "Brand",
                "name": "Gümüş Pestil"
              },
              "category": product.category?.name || "Pestil & Köme",
              "sku": product.barcode || product.id,
              "offers": {
                "@type": "Offer",
                "price": product.discountedPrice || product.price,
                "priceCurrency": "TRY",
                "availability": product.stockQuantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                "url": currentUrl,
                "seller": {
                  "@type": "Organization",
                  "name": "Gümüş Pestil"
                }
              },
              "aggregateRating": product.reviewCount > 0 ? {
                "@type": "AggregateRating",
                "ratingValue": product.rating,
                "reviewCount": product.reviewCount
              } : undefined,
              "weight": product.weight ? {
                "@type": "QuantitativeValue",
                "value": product.weight,
                "unitCode": "KGM"
              } : undefined,
              "nutrition": product.nutritionalInfo ? {
                "@type": "NutritionInformation",
                "description": product.nutritionalInfo
              } : undefined
            })}
          </script>
        )}
      </Helmet>

      {/* ColorInit */}
      <ColorInit color={false} />

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#299E60" />

      {/* Preloader */}
      <Preloader />

      {/* HeaderOne */}
      <HeaderOne />

      {/* Breadcrumb */}
      <Breadcrumb title={product ? product.name : "Ürün Detayı"} />

      {/* ProductDetailsOne */}
      <ProductDetailsOne productId={id} />

      {/* ShippingOne */}
      <ShippingOne />

      {/* FooterOne */}
      <FooterOne />

      {/* BottomFooter */}
      <BottomFooter />
    </>
  );
};

export default ProductDetailsPageOne;
